import * as React from "react";
import { Link, NavLink, useLocation, useNavigationType } from "react-router";
import { ArrowRight, BookOpen } from "lucide-react";
import { authClient } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { useLastPage } from "@/store/settings";

type HomepageLayoutProps = React.PropsWithChildren;

const TABS = [
	{ to: "/", label: "Krugovi", end: true },
	{ to: "/juzs", label: "Džuzevi", end: false },
	{ to: "/surahs", label: "Sure", end: false },
] as const;

/** Marks a navigation as a tab switch (see the scroll handling below). */
const TAB_STATE = { tabSwitch: true };

const tabClass = ({ isActive }: { isActive: boolean }) =>
	cn(
		"pressable grid h-10 place-items-center rounded-full px-4 text-sm md:h-9",
		isActive
			? "bg-emerald-900 font-semibold text-paper-50 shadow-card"
			: "font-medium text-ink-muted hover:text-ink",
	);

const HomepageLayout: React.FC<HomepageLayoutProps> = ({ children }) => {
	const { data: sessionData } = authClient.useSession();
	const { lastPage } = useLastPage();
	const location = useLocation();
	const navigationType = useNavigationType();
	const tabsAnchorRef = React.useRef<HTMLDivElement>(null);

	// Tabs keep the scroll position (preventScrollReset) instead of jumping to
	// the top of the hero. If the sticky bar was stuck, the new list starts
	// right under it rather than midway down.
	React.useLayoutEffect(() => {
		const anchor = tabsAnchorRef.current;
		const state = location.state as typeof TAB_STATE | null;
		if (!anchor || navigationType !== "PUSH" || !state?.tabSwitch) return;
		const top = anchor.getBoundingClientRect().top + window.scrollY;
		if (window.scrollY > top) window.scrollTo({ top });
	}, [location.key, location.state, navigationType]);

	const account = sessionData?.user.id
		? { to: "/admin", label: "Admin" }
		: { to: "/login", label: "Prijava" };
	const continuePage = lastPage ?? 1;

	return (
		<div className="flex min-h-svh flex-col font-sans">
			<header className="relative overflow-hidden bg-emerald-950 bg-girih text-paper-100">
				<div
					aria-hidden
					className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold-400/60 to-transparent"
				/>
				<div className="mx-auto max-w-5xl px-4 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-6">
					<div className="flex h-12 items-center justify-end">
						<Link
							to={account.to}
							className="pressable inline-flex h-11 items-center rounded-full px-3 text-xs font-semibold tracking-wide text-paper-200/80 hover:bg-white/5 hover:text-paper-50 focus-visible:outline-gold-300"
						>
							{account.label}
						</Link>
					</div>

					<div className="grid gap-8 pb-10 pt-4 md:grid-cols-[1fr_auto] md:items-end md:pb-14 md:pt-6">
						<div className="min-w-0">
							<p
								lang="ar"
								dir="rtl"
								className="w-fit font-arabic text-[28px] leading-none text-gold-300/85"
							>
								حفظ القرآن الكريم
							</p>
							<h1 className="mt-3 text-4xl font-semibold tracking-tight text-gold-300 md:text-5xl">
								Hifz Academy
							</h1>
							<p className="mt-3 max-w-xl text-base leading-relaxed text-paper-200/85 md:text-lg">
								Online platforma za učenje Kur'ana napamet sa primijenjenom
								metodom{" "}
								<a
									href="https://www.youtube.com/@KuranEdu-AhmedBukvic"
									target="_blank"
									rel="noopener noreferrer"
									className="font-semibold text-paper-50 underline decoration-gold-400/50 underline-offset-4 transition-colors duration-150 hover:decoration-gold-300 focus-visible:outline-gold-300"
								>
									hafiza Ahmeda Bukvića
								</a>
							</p>
							<div className="mt-5 flex flex-wrap items-center gap-2 text-sm">
								<span className="mr-1 text-paper-200/70 max-[379px]:w-full">Kontakt informacije:</span>
								<a
									href="viber:chat/?number=%2B38761372761"
									className="pressable inline-flex h-10 items-center gap-2 rounded-full bg-white/5 px-3.5 font-medium text-paper-50 ring-1 ring-white/10 hover:bg-white/10 focus-visible:outline-gold-300 md:h-9"
								>
									<span aria-hidden className="size-1.5 rounded-full bg-[#8f7cf7]" />
									Viber
								</a>
								<a
									href="https://wa.me/46767196659"
									className="pressable inline-flex h-10 items-center gap-2 rounded-full bg-white/5 px-3.5 font-medium text-paper-50 ring-1 ring-white/10 hover:bg-white/10 focus-visible:outline-gold-300 md:h-9"
								>
									<span aria-hidden className="size-1.5 rounded-full bg-[#3ddc84]" />
									WhatsApp
								</a>
							</div>
						</div>

						<Link
							to={`/${continuePage}`}
							className="pressable group flex items-center gap-4 rounded-2xl bg-gold-300 py-3.5 pl-4 pr-5 text-emerald-950 shadow-float hover:bg-gold-200 focus-visible:outline-gold-300 md:min-w-64"
						>
							<span className="grid size-10 shrink-0 place-items-center rounded-xl bg-emerald-950/10">
								<BookOpen aria-hidden className="size-5" />
							</span>
							<span className="min-w-0 flex-1">
								<span className="block text-sm font-semibold">
									{lastPage ? "Nastavi čitanje" : "Počni čitanje"}
								</span>
								<span className="block text-xs text-emerald-950/70">
									Stranica <strong className="tabular-nums">{continuePage}</strong>
								</span>
							</span>
							<ArrowRight
								aria-hidden
								className="size-4 shrink-0 transition-transform duration-150 ease-out group-hover:translate-x-0.5"
							/>
						</Link>
					</div>
				</div>
			</header>

			<main className="mx-auto min-h-[70svh] w-full max-w-5xl flex-1 px-4 sm:px-6">
				<div ref={tabsAnchorRef} aria-hidden />
				<div className="sticky top-0 z-30 -mx-4 border-b border-paper-300/70 bg-paper-50/85 px-4 pt-[env(safe-area-inset-top)] backdrop-blur-md sm:-mx-6 sm:px-6">
					<div className="flex flex-col gap-3 py-3 md:flex-row md:items-center md:justify-between md:py-4">
						<h2 className="text-sm font-medium text-ink-muted">
							Izaberi svoj način pretraživanja Kur'ana{" "}
							<span aria-hidden className="hidden md:inline">
								&rarr;
							</span>
						</h2>
						<nav
							aria-label="Način pretraživanja"
							className="grid grid-cols-3 gap-1 rounded-full bg-paper-200/80 p-1 ring-1 ring-paper-300 md:inline-grid"
						>
							{TABS.map((tab) => (
								<NavLink
									key={tab.to}
									to={tab.to}
									end={tab.end}
									state={TAB_STATE}
									preventScrollReset
									className={tabClass}
								>
									{tab.label}
								</NavLink>
							))}
						</nav>
					</div>
				</div>
				{children}
			</main>

			<footer className="mt-16 border-t border-paper-300 bg-paper-100">
				<div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-8 pb-[max(2rem,env(safe-area-inset-bottom))] sm:px-6">
					<p className="text-lg font-semibold tracking-tight text-emerald-900">
						Hifz Academy
					</p>
					<Link
						to={account.to}
						className="-mr-3 inline-flex h-11 items-center px-3 text-xs font-semibold tracking-wide text-ink-muted transition-colors duration-150 hover:text-emerald-900"
					>
						{account.label}
					</Link>
				</div>
			</footer>
		</div>
	);
};

export default React.memo(HomepageLayout);
